/** Providers globaux : Redux, thème MUI v9, router, error boundary, boot de session. */
import React, { useEffect, useMemo } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import type { ThunkDispatch } from "@reduxjs/toolkit";
import { BrowserRouter } from "react-router";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { getAppTheme } from "../theme/theme";
import type { AppStore, RootState } from "./store";
import { Agentation } from "agentation";
import { ErrorBoundary } from "./error-boundary";
import AppShell from "./AppShell";
import { authApi } from "../lib/api/authApi";
import { registerForPush, syncPushToken } from "../lib/mobile/push";
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { logger } from "../lib/observability/logger";
import "../lib/i18n";

export const Providers: React.FC<{ store: AppStore }> = ({ store }) => (
  <Provider store={store}>
    <ConnectedProviders />
  </Provider>
);

const ConnectedProviders: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, never>>();
  const colorMode = useSelector((s: RootState) => s.ui.colorMode);
  const sessionChecked = useSelector(
    (s: RootState) => s.session.sessionChecked,
  );
  const user = useSelector((s: RootState) => s.session.user);
  const theme = useMemo(() => getAppTheme(colorMode), [colorMode]);

  // Boot de session : si un token existe mais aucune session vérifiée,
  // on interroge le Worker une fois au démarrage (GET /auth/session).
  useEffect(() => {
    if (!sessionChecked) {
      dispatch(authApi.endpoints.session.initiate());
    }
  }, [dispatch, sessionChecked]);

  // Tailwind : la variante `dark:` doit suivre le même sélecteur que MUI.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', colorMode === 'dark');
  }, [colorMode]);

  // Capgo OTA : confirme que le bundle démarre correctement. Sans cet appel
  // (délai 10 s), le plugin marque le bundle défaillant et bascule sur le
  // précédent — c'est le mécanisme de rollback. No-op sur le web.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    void CapacitorUpdater.notifyAppReady().catch((error: unknown) => {
      logger.warn('capgo.notifyAppReady.failed', { error: String(error) });
    });
  }, []);

  // Réveil natif (FCM/APNs) : une fois connecté, sur Android/iOS uniquement.
  // No-op sur le web — aucune permission demandée, aucun jeton sollicité.
  useEffect(() => {
    if (!sessionChecked || !user) return;
    void (async () => {
      const token = await registerForPush();
      if (token) await syncPushToken(token);
    })();
  }, [sessionChecked, user]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <BrowserRouter>
          <Agentation />
          <AppShell />
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
